/**
 * An `atomFamily` whose key is an object of named ids. The family keeps its members under the
 * scope key of the object, so a key object that a render builds again gives the same member with
 * one map lookup.
 */

import type { Atom } from 'jotai';
import { atomFamily } from 'jotai-family';

import { getScopeKey } from '@/lib/scopeKey';
import type { ScopeKeyPart } from '@/types/common';
import type { KeyedFamily } from '@/types/store';

export function makeKeyedFamily<
    K extends { [F in keyof K]: ScopeKeyPart }, A extends Atom<unknown>,
>(init: (key: K) => A): KeyedFamily<K, A> {
    // Its entries live as long as the members of `atoms`, which hold their atoms the same way.
    const keys = new Map<string, K>();
    const atoms = atomFamily((text: string): A => {
        const key = keys.get(text);
        if (key === undefined) {
            throw new Error(`makeKeyedFamily: no key for ${text}`);
        }
        return init(key);
    });

    const family = (key: K): A => {
        const text = getScopeKey(key);
        if (!keys.has(text)) {
            keys.set(text, key);
        }
        return atoms(text);
    };
    family.getParams = (): K[] => [...atoms.getParams()].flatMap((text): K[] => {
        const key = keys.get(text);
        return key === undefined ? [] : [key];
    });
    family.remove = (key: K): void => {
        const text = getScopeKey(key);
        atoms.remove(text);
        keys.delete(text);
    };
    return family;
}
